import { useSelector } from "react-redux";
import { useRef, useState, useEffect } from "react";
import PlaceholderImage from "../../products/PlaceholderImage";
import ac from "../../../assets/smarthome/features/ac.jpg";
import energy from "../../../assets/smarthome/features/energy.jpg";
import accessControl from "../../../assets/smarthome/features/accessControl.jpg";
import heater from "../../../assets/smarthome/features/heater.jpg";
import light from "../../../assets/smarthome/features/light.jpg";
import scenarios from "../../../assets/smarthome/features/scenarios.jpg";
import notification from "../../../assets/smarthome/features/notification.jpg";
import shutters from "../../../assets/smarthome/features/shutters.jpg";
import solarPanel from "../../../assets/smarthome/features/solarPanel.jpg";
import sprinkler from "../../../assets/smarthome/features/sprinkler.jpg";
import wallSwitch from "../../../assets/smarthome/features/wallSwitch.jpg";
import waterHeater from "../../../assets/smarthome/features/waterHeater.jpg";

import energyIcon from "../../../assets/smarthome/features/energyIcon.png";
import notificationIcon from "../../../assets/smarthome/features/notificationIcon.png";
import wallSwitchIcon from "../../../assets/smarthome/features/wallSwitchIcon.png";
import acIcon from "../../../assets/smarthome/features/acIcon.png";
import shuttersIcon from "../../../assets/smarthome/features/shuttersIcon.png";
import solarPanelIcon from "../../../assets/smarthome/features/solarPanelIcon.png";
import heaterIcon from "../../../assets/smarthome/features/heaterIcon.png";
import accessControlIcon from "../../../assets/smarthome/features/accessControlIcon.png";
import lightIcon from "../../../assets/smarthome/features/lightIcon.png";
import scenariosIcon from "../../../assets/smarthome/features/scenariosIcon.png";
import waterHeaterIcon from "../../../assets/smarthome/features/waterHeaterIcon.png";
import sprinklerIcon from "../../../assets/smarthome/features/sprinklerIcon.png";

const features = [
  {
    name: "energy",
    image: energy,
    icon: energyIcon,
  },
  {
    name: "heater",
    image: heater,
    icon: heaterIcon,
  },
  {
    name: "ac",
    image: ac,
    icon: acIcon,
  },

  {
    name: "accessControl",
    image: accessControl,
    icon: accessControlIcon,
  },

  {
    name: "light",
    image: light,
    icon: lightIcon,
  },
  {
    name: "shutters",
    image: shutters,
    icon: shuttersIcon,
  },
  {
    name: "wallSwitch",
    image: wallSwitch,
    icon: wallSwitchIcon,
  },
  {
    name: "scenarios",
    image: scenarios,
    icon: scenariosIcon,
  },
  {
    name: "solarPanel",
    image: solarPanel,
    icon: solarPanelIcon,
  },
  {
    name: "waterHeater",
    image: waterHeater,
    icon: waterHeaterIcon,
  },
  {
    name: "notification",
    image: notification,
    icon: notificationIcon,
  },
  {
    name: "sprinkler",
    image: sprinkler,
    icon: sprinklerIcon,
  },
];

export default function FeaturesSection(props) {
  const { textContent, language } = { ...props };

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isFeatureHovered, setIsFeatureHovered] = useState(false);

  const isMobile = useSelector((state) => state.screen.isMobile);

  const scrollRef = useRef(null);

  const scrollFeatures = (direction) => {
    if (!scrollRef.current) return;

    const scrollAmount = 280; // ≈ largeur d’une colonne + gap
    scrollRef.current.scrollBy({
      left: direction * scrollAmount,
      behavior: "smooth",
    });
  };

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons(); // init

    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, []);

  const CustomScrollButton = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );

  return (
    <section className="container">
      <div className="container-small">
        <h1 className="title-gradient">
          {textContent.featuresSection.title[language]}
        </h1>
        <h2> {textContent.featuresSection.subtitle[language]}</h2>
        <h2 className="h2-bold">
          {textContent.featuresSection.features.title[language]}
        </h2>
      </div>
      <div className="container-empty-small" />
      <div className="features-carousel">
        {!isMobile && canScrollLeft && (
          <button
            className="carousel-btn left"
            aria-label="Précédent"
            onClick={() => scrollFeatures(-1)}
          >
            <CustomScrollButton />
          </button>
        )}
        <div className="features-scroll" ref={scrollRef}>
          <div className="grid-6">
            {features.map((feature, index) => (
              <div
                className="feature-card"
                key={index}
                onMouseEnter={() => {
                  setIsFeatureHovered(feature.name);
                }}
                onMouseLeave={() => {
                  setIsFeatureHovered(null);
                }}
              >
                <PlaceholderImage
                  src={feature.image}
                  crop={{ top: 0, right: 0, bottom: 0, left: 0 }}
                />

                <div className="feature-overlay">
                  <div className={`feature-text ${isMobile ? "mobile" : ""}`}>
                    <h4>
                      {
                        textContent.featuresSection.features.items[feature.name]
                          ?.title[language]
                      }
                    </h4>
                    {!isMobile && isFeatureHovered === feature.name ? (
                      <p>
                        {
                          textContent.featuresSection.features.items[
                            feature.name
                          ]?.description[language]
                        }
                      </p>
                    ) : null}
                  </div>
                  <div className={`feature-icon ${isMobile ? "mobile" : ""}`}>
                    <img src={feature.icon} alt="" aria-hidden="true" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {!isMobile && canScrollRight && (
          <button
            className="carousel-btn right"
            aria-label="Suivant"
            onClick={() => scrollFeatures(1)}
          >
            <CustomScrollButton />
          </button>
        )}
      </div>
    </section>
  );
}
